import { NavLink } from "react-router-dom";
import { Bell, Briefcase, CheckCircle2, FolderKanban, LayoutGrid, LogOut, MessageCircleQuestion, Monitor, Settings, StickyNote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { SPACES, SUPPORT_EMAIL } from "@/lib/spaces";
import { AtomMark } from "@/components/AtomMark";
import { SpaceAvatar } from "@/components/SpaceAvatar";

const NAV = [
  { to: "/", label: "Overview", icon: LayoutGrid, end: true, id: "overview" },
  { to: "/reminders", label: "Reminders", icon: Bell, id: "reminders" },
  { to: "/completed", label: "Completed", icon: CheckCircle2, id: "completed" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "projects" },
  { to: "/notes", label: "Notes", icon: StickyNote, id: "notes" },
  { to: "/thoughts", label: "To discuss", icon: MessageCircleQuestion, id: "thoughts" },
  { to: "/wallboard", label: "TV wallboard", icon: Monitor, id: "wallboard" },
  { to: "/sales", label: "Sales", icon: Briefcase, id: "sales" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

export const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { space, setSpace } = useApp();
  return (
    <div className="flex h-full flex-col px-4 py-6">
      <div className="flex items-center gap-3 px-2">
        <AtomMark size={30} />
        <div>
          <p className="font-display text-base font-bold leading-none tracking-tight">Atom IP</p>
          <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Team board</p>
        </div>
      </div>
      <nav className="mt-10 space-y-1">
        {NAV.map(({ to, label, icon: Icon, end, id }) => (
          <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={`nav-${id}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-white/[0.07] text-foreground" : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <p className="mt-10 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Spaces</p>
      <div className="mt-3 space-y-1">
        {SPACES.map((s) => (
          <button key={s.key} data-testid={`space-filter-${s.key}`} onClick={() => { setSpace(s.key); onNavigate?.(); }}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${space === s.key ? "text-foreground" : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"}`}
            style={space === s.key ? { background: `${s.color}1a`, boxShadow: `inset 2px 0 0 ${s.color}` } : {}}>
            <SpaceAvatar space={s.key} />
            {s.name}
          </button>
        ))}
      </div>
      <div className="mt-auto space-y-4">
        <p className="rounded-lg border border-dashed px-3 py-2.5 text-[11px] leading-relaxed text-muted-foreground">
          Every reminder is copied to <span className="text-foreground">{SUPPORT_EMAIL}</span>, plus a digest every 3 hours.
        </p>
        <div className="flex items-center gap-3 rounded-xl border bg-card/60 p-3">
          <SpaceAvatar space={user.username} size="lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user.email}</p>
          </div>
          <button onClick={logout} data-testid="logout-button" title="Sign out" className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
